import PropTypes from "prop-types";

import { useMemo } from "react";

import { Empty, Spin } from "antd";

import { useTranslation } from "react-i18next";

import CustomPagination from "../../../components/custom-component/CustomPagination";
import CustomSegmentedTabs, { useSegmentedTabState } from "../../../components/custom-component/CustomSegmentedTabs";

import InsuranceCard from "./cards/InsuranceCard";

const PROVIDER_BONZAH = "bonzah";
const PROVIDER_TEQ_MOBILITY = "teqMobility";
// INSHUR-DISABLED: the INSHUR tab is hidden; a `?provider=abi` URL falls back to the default tab.
// const PROVIDER_ABI = "abi";

const InsuranceAddons = ({ insuranceAddons, initialFetching, handleModalState, pagination, setPagination }) => {
    const { t } = useTranslation(["addons"]);
    const { activeTab: activeProvider, handleTabChange } = useSegmentedTabState({
        defaultValue: PROVIDER_BONZAH,
        urlParam: "provider",
        // INSHUR-DISABLED: restore PROVIDER_ABI here when INSHUR comes back.
        validValues: [PROVIDER_BONZAH, PROVIDER_TEQ_MOBILITY],
    });

    const filteredAddons = useMemo(() => {
        if (!insuranceAddons || insuranceAddons.length === 0) return [];
        return insuranceAddons.filter((addon) => addon?.provider === activeProvider);
    }, [insuranceAddons, activeProvider]);

    const handlePageChange = (page) => {
        setPagination((prev) => ({ ...prev, currentPage: page }));
    };

    const handlePageSizeChange = (size) => {
        setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
    };

    const handleProviderChange = (value) => {
        handleTabChange(value);
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
    };

    const tabItems = [
        { label: t("createAddons.bonzah"), value: PROVIDER_BONZAH },
        { label: t("createAddons.teqMobility"), value: PROVIDER_TEQ_MOBILITY },
        // INSHUR-DISABLED
        // { label: t("createAddons.abi"), value: PROVIDER_ABI },
    ];

    return (
        <div className="min-w-0">
            <CustomSegmentedTabs
                items={tabItems}
                value={activeProvider}
                onChange={handleProviderChange}
                segmentedClassName="mb-4 w-full bg-[#F2F2FA] sm:bg-white"
            />

            <div>
                {initialFetching ? (
                    <div className="flex justify-center items-center h-full">
                        <Spin />
                    </div>
                ) : filteredAddons?.length > 0 ? (
                    <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-4 lg:gap-4">
                        {filteredAddons?.map((addon) => (
                            <div key={addon?.id} className="h-full">
                                <InsuranceCard addon={addon} handleModalState={handleModalState} />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex justify-center items-center h-full">
                        <Empty />
                    </div>
                )}
                <div className="mt-4 flex min-w-0 w-full justify-end">
                    <CustomPagination
                        variant="spaced"
                        totalItem={filteredAddons?.length || 0}
                        pageSize={pagination.pageSize}
                        setPageSize={handlePageSizeChange}
                        currentPage={pagination.currentPage}
                        setCurrentPage={handlePageChange}
                        showSizeChanger={false}
                    />
                </div>
            </div>
        </div>
    );
};

InsuranceAddons.propTypes = {
    insuranceAddons: PropTypes.array.isRequired,
    initialFetching: PropTypes.bool.isRequired,
    handleModalState: PropTypes.func.isRequired,
    pagination: PropTypes.shape({
        currentPage: PropTypes.number.isRequired,
        pageSize: PropTypes.number.isRequired,
        total: PropTypes.number.isRequired,
    }).isRequired,
    setPagination: PropTypes.func.isRequired,
};

export default InsuranceAddons;
